import { useEffect, useCallback } from 'react';

/**
 * Custom hook for wizard keyboard navigation
 * 
 * Provides keyboard shortcuts:
 * - Enter: Proceed to next step (if canGoNext)
 * - Backspace/Alt+Left: Go to previous step (if canGoPrevious)
 * - Escape: Cancel current action or close modals
 * 
 * @param {Object} options
 * @param {Function} options.onNext - Callback for next step
 * @param {Function} options.onPrevious - Callback for previous step
 * @param {Function} options.onEscape - Callback for escape action (optional)
 * @param {boolean} options.canGoNext - Whether next is allowed
 * @param {boolean} options.canGoPrevious - Whether previous is allowed
 * @param {boolean} options.enabled - Whether keyboard navigation is active (default: true)
 */
export function useWizardKeyboard({
    onNext,
    onPrevious,
    onEscape,
    canGoNext = true,
    canGoPrevious = true,
    enabled = true
}) {
    const handleKeyDown = useCallback((event) => {
        // Don't handle if navigation is disabled
        if (!enabled) return;

        // Don't handle if user is typing in an input/textarea/select
        const activeElement = document.activeElement;
        const isInputField =
            activeElement?.tagName === 'INPUT' ||
            activeElement?.tagName === 'TEXTAREA' ||
            activeElement?.tagName === 'SELECT' ||
            activeElement?.isContentEditable;

        // For Enter, only proceed if not in a multi-line textarea
        if (event.key === 'Enter' && !event.shiftKey) {
            // Allow Enter in textareas (for newlines) and selects (for selection)
            if (activeElement?.tagName === 'TEXTAREA') return;
            // Allow Enter in inputs to submit forms naturally, but we can also trigger next
            if (activeElement?.tagName === 'INPUT' && activeElement?.type !== 'button') {
                // If it's a text/email/etc input, let default behavior happen
                // but we can proceed to next if there's a valid value
            }

            if (canGoNext && onNext) {
                event.preventDefault();
                onNext();
            }
        }

        // Backspace (when not in input) or Alt+Left for previous
        if (
            (event.key === 'Backspace' && !isInputField) ||
            (event.key === 'ArrowLeft' && event.altKey)
        ) {
            if (canGoPrevious && onPrevious) {
                event.preventDefault();
                onPrevious();
            }
        }

        // Alt+Right for next (alternative)
        if (event.key === 'ArrowRight' && event.altKey) {
            if (canGoNext && onNext) {
                event.preventDefault();
                onNext();
            }
        }

        // Escape for cancel/close
        if (event.key === 'Escape') {
            if (onEscape) {
                event.preventDefault();
                onEscape();
            }
        }
    }, [enabled, onNext, onPrevious, onEscape, canGoNext, canGoPrevious]);

    useEffect(() => {
        if (enabled) {
            window.addEventListener('keydown', handleKeyDown);
            return () => window.removeEventListener('keydown', handleKeyDown);
        }
    }, [enabled, handleKeyDown]);

    return {
        // Expose helper to check if shortcuts are active
        isActive: enabled
    };
}

/**
 * Helper component to show keyboard shortcuts hint
 */
export const KeyboardShortcutsHint = ({ show = true, className = '' }) => {
    if (!show) return null;

    return (
        <div className={`text-xs text-gray-400 text-center mt-4 ${className}`}>
            <span className="hidden sm:inline">
                Press <kbd className="px-1.5 py-0.5 rounded bg-gray-100 text-gray-600 font-mono">Enter</kbd> to continue
                {' • '}
                <kbd className="px-1.5 py-0.5 rounded bg-gray-100 text-gray-600 font-mono">Alt</kbd>+
                <kbd className="px-1.5 py-0.5 rounded bg-gray-100 text-gray-600 font-mono">←</kbd> to go back
            </span>
        </div>
    );
};

export default useWizardKeyboard;
